/**
 * CalendarBoard — tryb „Kalendarz" dla ConfigurableTable (silnik list).
 *
 * Generyczny odpowiednik `KanbanBoard`: pozycjonuje wiersze po kolumnie daty
 * wskazanej w `calendarConfig.startFieldSlug`. Opcjonalny `endFieldSlug` rozciąga wpis na
 * zakres [start, end]; opcjonalny `statusFieldSlug` (kolumna select) koloruje wpisy
 * kolorem z `headerFilter.options[].color` + rysuje legendę; etykieta z
 * `labelFieldSlug`. Bez ustawionego `labelFieldSlug` etykietę daje zapasowa kolumna WIDOCZNA: gdy
 * `titleColumnId` nie podano — pierwsza widoczna; gdy podano, a ta kolumna jest ukryta — skrócone id
 * wiersza (bez dalszego zapasu). Gdy `labelFieldSlug` jest ustawiony, ale żadna kolumna strony go nie
 * rysuje, etykietą jest skrócone id wiersza (bez zapasowej kolumny tytułowej). Bez skonfigurowanego
 * pola startu — pusty stan z prośbą o konfigurację (parytet z Kanbanem).
 *
 * Pola kalendarza (start / koniec / status / etykieta) są szukane wśród WSZYSTKICH definicji
 * kolumn strony (`fieldColumns`), nie wśród widocznych — kalendarz nie zależy od tego, które
 * kolumny użytkownik pokazał na liście. Wartości ukrytych kolumn dostarcza do wierszy sama strona:
 * dopisuje slugi pól kalendarza do `?columns=` niezależnie od widoczności
 * (`computeVisibleColumnParams`, argument `calendarConfig`). Widoczne kolumny (`columns`) służą
 * tylko za zapasową kolumnę etykiety (gdy `labelFieldSlug` nie jest ustawiony).
 *
 * Zakres miesiąca emitowany w górę (`onRangeChange`) niesie slug pola startu i — gdy skonfigurowano —
 * pola końca (rozwiązany tak samo jak start): serwer zwraca wtedy rekordy, których przedział
 * nachodzi na miesiąc, więc wpis 28.10–05.11 widać i w październiku, i w listopadzie.
 *
 * Zapytanie listy w trybie kalendarza pobiera do `CALENDAR_FETCH_LIMIT` rekordów miesiąca naraz
 * (`useServerListControls`). Gdy serwer zgłasza więcej (`totalItems` > wczytane wiersze), nad siatką
 * pojawia się komunikat „pokazano X z Y" — siatka nie rysuje po cichu części miesiąca.
 *
 * Renderem jest wspólny `MonthGrid` (ten sam co w `CalendarView` modułów custom).
 */

import { isValidElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CalendarConfig, ColumnConfig } from '@saas/shared';

import {
  type CalendarEvent,
  DEFAULT_ENTRY_COLOR,
  extractDateStr,
  STATUS_FALLBACK_COLOR,
} from '@/components/calendar/calendar-utils';
import { MonthGrid } from '@/components/calendar/MonthGrid';

import type { CalendarRange, ColumnDefinition } from './types';

interface CalendarBoardProps<T> {
  /**
   * Widoczne kolumny listy — tylko zapasowa kolumna etykiety wpisu (gdy brak `labelFieldSlug`):
   * gdy `titleColumnId` nie podano — pierwsza widoczna; gdy podano, a ta kolumna jest ukryta —
   * skrócone id wiersza. Wiersze na pewno niosą wartości tych kolumn; pola kalendarza z
   * konfiguracji biorą się z `fieldColumns`.
   */
  columns: ColumnDefinition<T>[];
  /**
   * WSZYSTKIE definicje kolumn strony (także ukryte w układzie) — źródło pól kalendarza z
   * `calendarConfig`. Pole konfiguracji dopasowujemy do definicji po slugu: najpierw
   * `column.id`, potem `column.fieldSlug` (slug pola backendu niezależny od filtra), na końcu
   * `column.headerFilter.fieldSlug` (zapisana wartość bywa id kolumny albo slugiem pola z katalogu,
   * a te dwa nie zawsze się pokrywają — np. kolumna `company` ↔ pole `company_id`).
   * Pole bez żadnej definicji kolumny: dla startu — pusty stan „kalendarz nie jest
   * skonfigurowany"; dla końca — wpis rysowany w jednym dniu (bez zakresu); dla statusu — domyślny
   * kolor, bez legendy; dla etykiety — skrócone id wiersza (NIE kolumna tytułowa: ta jest zapasem
   * tylko, gdy `labelFieldSlug` w ogóle nie ustawiono).
   */
  fieldColumns: ColumnDefinition<T>[];
  /** Dane do wyświetlenia (już przetworzone przez ConfigurableTable). */
  data: T[];
  getRowId: (row: T) => string;
  getCellValue: (row: T, column: ColumnDefinition<T>) => unknown;
  getColumnConfig: (columnId: string) => ColumnConfig | undefined;
  /** Konfiguracja kalendarza z layoutu (pole startu + opcjonalne koniec/status/etykieta). */
  calendarConfig?: CalendarConfig;
  /**
   * Kolumna tytułowa listy — fallback etykiety wpisu (gdy brak `labelFieldSlug`). Szukana wśród
   * WIDOCZNYCH kolumn (`columns`): tylko ich wartości strona gwarantuje w wierszach.
   */
  titleColumnId?: string;
  onRowClick?: (row: T) => void;
  /**
   * Emituje zakres dat widocznego miesiąca `{ start, end, fieldSlug, endFieldSlug? }` przy montażu
   * oraz przy każdej nawigacji miesiąca; `null` przy odmontowaniu (wyjście z trybu kalendarza).
   * Strona wpina to w zapytanie listy jako filtr zakresu, żeby dociągać z bazy tylko rekordy,
   * których przedział nachodzi na ten miesiąc. `endFieldSlug` — tylko gdy pole końca z konfiguracji
   * ma definicję kolumny na stronie (jak przy rysowaniu zakresu).
   *
   * WYMÓG: handler musi mieć STABILNĄ referencję (np. `useCallback([])` — tak działa
   * `useServerListControls.setCalendarRange`). Efekty emisji/cleanup zależą od jego tożsamości;
   * niememoizowany handler powodowałby churn null↔zakres (zbędne refetche) przy każdym renderze.
   */
  onRangeChange?: (range: CalendarRange | null) => void;
  /**
   * Liczba wszystkich rekordów miesiąca wg serwera (`total` listy). Większa niż liczba wczytanych
   * wierszy (`data`) → komunikat nad siatką „pokazano X z Y", bo zapytanie kalendarza pobiera
   * najwyżej `CALENDAR_FETCH_LIMIT` rekordów naraz. Brak = bez komunikatu.
   */
  totalItems?: number;
  /** Siatka wypełnia wysokość rodzica (rzędy tygodni rozciągają się) — patrz `MonthGrid.fillHeight`. */
  fillHeight?: boolean;
  /**
   * Dane są w trakcie dociągania (np. `keepPreviousData` po zmianie miesiąca) — tłumi komunikat
   * pustego miesiąca, żeby nie migał fałszywie na starych wierszach filtrowanych wg nowego miesiąca.
   * Tłumi też komunikat „kalendarz nieskonfigurowany" (brak definicji kolumny pola startu wśród
   * `fieldColumns` — np. katalog pól jeszcze się wczytuje): zamiast niego puste miejsce tej samej
   * wysokości.
   */
  loading?: boolean;
  testIdPrefix?: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Wyciąga zwykły tekst z ReactNode zwróconego przez `renderCell` kolumny etykiety.
 *
 * `CalendarEvent.label` (`calendar-utils.ts`) jest kontraktowo `string` — używa go też
 * atrybut HTML `title` w `MonthGrid` — więc bogaty render (ikony, kolor) nie ma jak
 * dotrzeć na chip; wyciągamy WYŁĄCZNIE tekst z prostych przypadków (string/number,
 * pojedynczy element z tekstowym dzieckiem — dokładnie kształt dzisiejszych rendererów
 * kolumn referencyjnych, np. `renderCoreReferenceCell` w `ext-list-columns.tsx`:
 * `<span>{name}</span>`). Wiele dzieci (tablica chipów, fragment) → teksty dzieci złączone
 * przecinkiem — pole wielokrotnego wyboru wybrane jako etykieta pokaże listę nazw, nie
 * `[object Object]`. Drzewo bez żadnego tekstu → `null`, wołający spada na fallback `String(raw)`.
 */
function extractPlainText(node: ReactNode): string | null {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const parts = node
      .map((child) => extractPlainText(child as ReactNode))
      .filter((part): part is string => part != null && part !== '');
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (isValidElement(node)) {
    // Bezpieczne po `isValidElement` — element React zawsze ma `props`, a `children`
    // (jeśli jest) to ReactNode; rzutowanie tylko zawęża typ `any` z `ReactElement`.
    const children = (node.props as { children?: ReactNode }).children;
    return extractPlainText(children);
  }
  return null;
}

/**
 * Definicja kolumny pola z konfiguracji kalendarza (`startFieldSlug` itd.) wśród kolumn strony.
 * Kolejność dopasowania: `column.id`, potem `column.fieldSlug` (slug pola backendu niezależny od
 * filtra), na końcu `column.headerFilter.fieldSlug` — zapisana wartość to id kolumny (układy z
 * Kreatora sprzed katalogu pól) albo slug pola z katalogu, a kolumna wirtualna lub złożona ma `id`
 * inny niż slug pola (np. `company` ↔ `company_id`). `null` = żadna kolumna strony nie rysuje tego
 * pola.
 */
function findFieldColumn<T>(
  columns: ColumnDefinition<T>[],
  slug: string | undefined,
): ColumnDefinition<T> | null {
  if (!slug) return null;
  return (
    columns.find((c) => c.id === slug) ??
    columns.find((c) => c.fieldSlug === slug) ??
    columns.find((c) => c.headerFilter?.fieldSlug === slug) ??
    null
  );
}

export function CalendarBoard<T>({
  columns,
  fieldColumns,
  data,
  getRowId,
  getCellValue,
  getColumnConfig,
  calendarConfig,
  titleColumnId,
  onRowClick,
  onRangeChange,
  totalItems,
  fillHeight,
  loading,
  testIdPrefix,
}: CalendarBoardProps<T>) {
  const { t } = useTranslation();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  // Pola kalendarza z WSZYSTKICH kolumn strony (nie tylko widocznych) — patrz JSDoc `fieldColumns`.
  const startColumn = useMemo(
    () => findFieldColumn(fieldColumns, calendarConfig?.startFieldSlug),
    [fieldColumns, calendarConfig?.startFieldSlug],
  );
  const endColumn = useMemo(
    () => findFieldColumn(fieldColumns, calendarConfig?.endFieldSlug),
    [fieldColumns, calendarConfig?.endFieldSlug],
  );
  const statusColumn = useMemo(
    () => findFieldColumn(fieldColumns, calendarConfig?.statusFieldSlug),
    [fieldColumns, calendarConfig?.statusFieldSlug],
  );
  const labelColumn = useMemo(() => {
    const slug = calendarConfig?.labelFieldSlug;
    if (slug) return findFieldColumn(fieldColumns, slug);
    // Bez wskazanego pola etykiety: kolumna tytułowa, potem pierwsza WIDOCZNA — tylko wśród widocznych,
    // bo tylko ich wartości strona gwarantuje w wierszach.
    if (titleColumnId) return columns.find((c) => c.id === titleColumnId) ?? null;
    return columns[0] ?? null;
  }, [fieldColumns, columns, calendarConfig?.labelFieldSlug, titleColumnId]);

  // Slug backendu pola daty startu (prymityw — stabilny dep dla efektu zakresu; nie sam obiekt
  // kolumny, który bywa nową referencją co render): slug filtra kolumny, a bez filtra — slug pola
  // kolumny (`fieldSlug`), na końcu jej id. Null bez skonfigurowanego pola startu.
  const startFieldSlug = startColumn
    ? (startColumn.headerFilter?.fieldSlug ?? startColumn.fieldSlug ?? startColumn.id)
    : null;
  // Slug backendu pola daty końca — ta sama reguła co dla startu. Null bez pola końca albo bez jego
  // definicji kolumny (wtedy wpis i tak rysuje się w jednym dniu, a serwer filtruje po starcie).
  const endFieldSlug = endColumn
    ? (endColumn.headerFilter?.fieldSlug ?? endColumn.fieldSlug ?? endColumn.id)
    : null;

  // Emituje zakres widocznego miesiąca w górę przy montażu i każdej nawigacji miesiąca — strona
  // dociąga wtedy z bazy tylko rekordy, których przedział nachodzi na ten miesiąc (filtr `range`
  // w silniku list; z polem końca — warunek nakładania się, bez niego — po samej dacie startu).
  useEffect(() => {
    if (!onRangeChange) return;
    if (!startFieldSlug) {
      onRangeChange(null);
      return;
    }
    const start = `${year}-${pad2(month + 1)}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const end = `${year}-${pad2(month + 1)}-${pad2(lastDay)}`;
    onRangeChange({
      start,
      end,
      fieldSlug: startFieldSlug,
      ...(endFieldSlug ? { endFieldSlug } : {}),
    });
  }, [year, month, startFieldSlug, endFieldSlug, onRangeChange]);

  // Wyjście z trybu kalendarza (odmontowanie) zeruje zakres, żeby filtr miesiąca nie „przykleił się"
  // do zapytań innych trybów widoku. Osobny efekt (nie cleanup powyższego) — inaczej null leciałby
  // też przy każdej zmianie miesiąca, powodując zbędny refetch bez filtra.
  useEffect(() => {
    return () => onRangeChange?.(null);
  }, [onRangeChange]);

  // Metadane opcji statusu (value → kolor) do kolorowania wpisów + legendy.
  const statusOptions = statusColumn?.headerFilter?.options ?? [];
  const statusColorByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of statusOptions) map.set(opt.value, opt.color ?? STATUS_FALLBACK_COLOR);
    return map;
  }, [statusOptions]);

  const { events, noDateEvents } = useMemo(() => {
    const withDate: CalendarEvent[] = [];
    const without: CalendarEvent[] = [];
    if (!startColumn) return { events: withDate, noDateEvents: without };

    for (const [rowIndex, row] of data.entries()) {
      const id = getRowId(row);

      // Etykieta: kolumna etykiety/tytułu → wartość (przez renderer kolumny, gdy dostępny —
      // np. pole referencyjne pokazuje rozwiązaną nazwę zamiast surowego UUID), fallback
      // surowy string, dalszy fallback skrócone id.
      let label = id.slice(0, 8);
      if (labelColumn) {
        const raw = getCellValue(row, labelColumn);
        if (raw != null && raw !== '') {
          label = String(raw);
          if (labelColumn.renderCell) {
            const rendered = extractPlainText(labelColumn.renderCell(raw, row, rowIndex));
            if (rendered != null && rendered !== '') label = rendered;
          }
        }
      }

      // Kolor: z opcji pola statusu, fallback szary; bez pola statusu — domyślny niebieski.
      let color = DEFAULT_ENTRY_COLOR;
      if (statusColumn) {
        const raw = getCellValue(row, statusColumn);
        const key = raw == null ? '' : String(raw);
        color = statusColorByValue.get(key) ?? STATUS_FALLBACK_COLOR;
      }

      const startKey = extractDateStr(getCellValue(row, startColumn));
      if (!startKey) {
        without.push({ id, label, color, startKey: '', endKey: '' });
        continue;
      }
      const rawEnd = endColumn ? extractDateStr(getCellValue(row, endColumn)) : null;
      const endKey = rawEnd && rawEnd >= startKey ? rawEnd : startKey;
      withDate.push({ id, label, color, startKey, endKey });
    }
    return { events: withDate, noDateEvents: without };
  }, [
    data,
    startColumn,
    endColumn,
    statusColumn,
    labelColumn,
    statusColorByValue,
    getRowId,
    getCellValue,
  ]);

  const rowsById = useMemo(() => {
    const map = new Map<string, T>();
    for (const row of data) map.set(getRowId(row), row);
    return map;
  }, [data, getRowId]);

  const legend =
    statusColumn && statusOptions.length > 0
      ? statusOptions.map((o) => ({
          label: o.label ?? o.value,
          color: o.color ?? STATUS_FALLBACK_COLOR,
        }))
      : undefined;

  // `getColumnConfig` zarezerwowane pod przyszłe użycie (parytet sygnatury z KanbanBoard);
  // referencja, żeby lint nie zgłaszał nieużywanego propa bez zmiany kontraktu.
  void getColumnConfig;

  // Miesiąc ma więcej wpisów, niż zapytanie kalendarza pobiera naraz — mówimy o tym wprost zamiast
  // rysować po cichu część. Na czas dociągania (`loading`) bez komunikatu: wiersze i licznik mogą
  // jeszcze dotyczyć poprzedniego miesiąca.
  const truncatedNotice =
    !loading && totalItems !== undefined && totalItems > data.length ? (
      <p
        role="status"
        className="shrink-0 rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-center text-sm text-text-secondary"
        data-testid={testIdPrefix ? `${testIdPrefix}-calendar-truncated` : 'calendar-truncated'}
      >
        {t('tenant.listBuilder.viewMode.calendar_meta.truncated', {
          shown: data.length,
          total: totalItems,
        })}
      </p>
    ) : undefined;

  if (!startColumn) {
    // W trakcie wczytywania (np. kolumny z katalogu pól jeszcze nie rozwiązane) komunikat
    // „nieskonfigurowany" byłby fałszywy — zostawiamy puste miejsce tej samej wysokości (pod
    // nakładką ładowania tabeli), bez tekstu.
    if (loading) {
      return <div className="px-4 py-16" aria-hidden="true" />;
    }
    return (
      <div
        className="flex flex-col items-center justify-center px-4 py-16 text-center"
        data-testid={testIdPrefix ? `${testIdPrefix}-calendar-empty` : undefined}
      >
        <p className="text-sm font-medium text-text-secondary">
          {t('tenant.listBuilder.viewMode.calendar_meta.notConfigured')}
        </p>
        <p className="mt-1 text-xs text-text-tertiary">
          {t('tenant.listBuilder.viewMode.calendar_meta.notConfiguredHint')}
        </p>
      </div>
    );
  }

  return (
    <MonthGrid
      year={year}
      month={month}
      events={events}
      noDateEvents={noDateEvents}
      legend={legend}
      onPrevMonth={() => {
        if (month === 0) {
          setYear((y) => y - 1);
          setMonth(11);
        } else setMonth((m) => m - 1);
      }}
      onNextMonth={() => {
        if (month === 11) {
          setYear((y) => y + 1);
          setMonth(0);
        } else setMonth((m) => m + 1);
      }}
      onToday={() => {
        setYear(today.getFullYear());
        setMonth(today.getMonth());
      }}
      onEventClick={(eventId) => {
        const row = rowsById.get(eventId);
        if (row && onRowClick) onRowClick(row);
      }}
      suppressEmptyMessage={loading}
      notice={truncatedNotice}
      fillHeight={fillHeight}
      testId={testIdPrefix ? `${testIdPrefix}-calendar` : 'calendar-view'}
    />
  );
}
